import React, { useRef, useState } from 'react';
import { Eye, FileText, Trash2, Upload } from 'lucide-react';
import apiClient from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export const ProfileDocuments = ({ student, onChanged }) => {
  const [source, setSource] = useState('student');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File size exceeds 10MB'); event.target.value = ''; return; }
    setBusy(true);
    try {
      const data = new FormData();
      data.append('file', file); data.append('source', source); data.append('category', 'other'); data.append('document_type', 'general');
      await apiClient.post(`/students/${student.id}/documents`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      await onChanged(); toast.success('Document uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); event.target.value = ''; }
  };
  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.name}?`)) return;
    try { await apiClient.delete(`/students/${student.id}/documents/${doc.id}`); await onChanged(); toast.success('Document deleted'); }
    catch { toast.error('Could not delete document'); }
  };
  return <section className="space-y-6" data-testid="profile-documents">
    <div className="flex flex-wrap gap-3 items-end justify-between">
      <div className="space-y-1"><label htmlFor="document-source" className="text-xs text-gray-500">Document source</label>
        <select id="document-source" value={source} onChange={e => setSource(e.target.value)} disabled={busy} data-testid="doc-source-select" className="block min-h-[44px] rounded-md border bg-white px-3 text-sm"><option value="student">Student</option><option value="university">University</option></select>
      </div>
      <input ref={inputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid="document-upload-input" onChange={upload} disabled={busy} />
      <Button onClick={() => inputRef.current?.click()} disabled={busy} className="min-h-[44px]" data-testid="upload-document-btn"><Upload className="w-4 h-4 mr-2" />{busy ? 'Uploading…' : 'Upload document'}</Button>
    </div>
    {['student', 'university'].map(group => {
      const docs = (student.documents || []).filter(d => group === 'university' ? d.source === group : d.source !== 'university');
      return <div key={group} data-testid={`${group}-documents-group`}>
        <h2 className="text-base font-semibold mb-3">{group === 'student' ? 'Student documents' : 'University documents'} <span className="font-normal text-gray-500">({docs.length})</span></h2>
        {docs.length ? <div className="divide-y">{docs.map(doc => <div key={doc.id} className="py-3 flex gap-3 items-start" data-testid={`document-${doc.id}`}>
          <FileText className="w-5 h-5 text-gray-400 mt-2 shrink-0" />
          <div className="min-w-0 flex-1"><p className="text-sm font-medium break-words">{doc.name}</p><p className="text-xs text-gray-500 break-words mt-1">{doc.uploaded_by_name || 'Staff'} · {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : 'Date not recorded'}{doc.file_size ? ` · ${Math.round(doc.file_size / 1024)} KB` : ''}</p>
            <div className="flex gap-2 mt-2">
              {doc.file_path && <Button asChild variant="outline" size="sm" className="min-h-[44px]"><a href={doc.file_path} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${doc.id}`}><Eye className="w-4 h-4 mr-2" />View</a></Button>}
              <Button variant="ghost" size="sm" className="min-h-[44px] text-red-600" onClick={() => remove(doc)} data-testid={`delete-doc-${doc.id}`} aria-label={`Delete ${doc.name}`}><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
            </div>
          </div>
        </div>)}</div> : <p className="text-sm text-gray-500" data-testid={`${group}-documents-empty`}>No documents yet</p>}
      </div>;
    })}
  </section>;
};